import { alertToast } from '@/utilities/notification'
import * as Sentry from '@sentry/vue'
import { Router } from 'vue-router'

import updateAbility from '@/config/update-ability.js'

import { defineStore } from 'pinia'

import useApiRequest from '@/composables/useApiRequest'

// stores
import { useUserSettingsStore } from './UserSettingsStore'
import { useTaskStore } from './TaskStore'
import { useSocketStore } from './SocketStore'
import { useCallCenterStore } from './CallCenterStore'

// Types
interface UserSettings {
  darkmode: string
  locale: string
  timezone: string
  clock24Hours: boolean
}

interface AccessAllowed {
  regions: string[]
  manufacturers: string[]
  groups: string[]
  dealerships: {
    id: number
    name: string
    manufacturers: number[]
  }[]
}
interface UserDetails {
  id: string | null
  userType: string | null
  level?: string
  email: string | null
  name: string | null
  forename: string | null
  surname: string | null
  initials: string | null
  profilePicUrl: string | null
}

interface LoginCredentials {
  e: string
  p: string
}

interface LoginResponse {
  success: boolean
  message?: string
}

interface VerifyResponse {
  success: boolean
  message: string | null
}

interface UserPayload {
  uid: string
  ut: string
  ul?: string
  email: string
  userName: string
  userForename: string
  userSurname: string
  profilePicUrl: string
  userType: string
}

interface AuthData {
  payload: UserPayload
  token?: string // Legacy field from API response, not used but still present in response
}

interface UpdateSentryScopeData {
  userType: string
  uid: string
  email: string
}

export
  /**
   * Updates Sentry scope with user information
   * @param data - User data for Sentry scope
   */
  function updateSentryScope(data: UpdateSentryScopeData): void {
  Sentry.getCurrentScope().setTag('user-type', data.userType)
  Sentry.getCurrentScope().setUser({
    id: data.uid,
    email: data.email
  })
}

export const useAuthStore = defineStore('authStore', {
  state: (): {
    details: UserDetails
    isLoggedIn: boolean
    isAuthenticating: boolean
    isVerifying: boolean
    router?: Router
    roles: string[]
    accessAllowed: AccessAllowed
  } => ({
    details: {
      id: null,
      userType: null,
      email: null,
      name: null,
      forename: null,
      surname: null,
      initials: null,
      profilePicUrl: null
    },
    isLoggedIn: false,
    isAuthenticating: false,
    isVerifying: false,
    roles: [],
    accessAllowed: {
      regions: [],
      manufacturers: [],
      groups: [],
      dealerships: []
    }
  }),

  getters: {
    /**
     * Gets the current user ID
     */
    userId(): string | null {
      return this.details.id
    },

    /**
     * Checks if user is authenticated
     */
    isAuthenticated(): boolean {
      return this.isLoggedIn && this.details.id !== null
    },

  },

  actions: {
    /**
     * Sets user details from authentication data
     * @param data - Authentication data containing user payload
     */
    async setUserDetails(data: AuthData): Promise<void> {
      // Get first character of forename and surname to create initials
      const initials: string = data.payload.userForename.charAt(0) + data.payload.userSurname.charAt(0)

      // if new details are not the same as the old ones, update the sentry scope
      if (
        this.details.userType !== data.payload.ut ||
        this.details.id !== data.payload.uid ||
        this.details.email !== data.payload.email ||
        this.details.name !== data.payload.userName ||
        this.details.forename !== data.payload.userForename ||
        this.details.surname !== data.payload.userSurname ||
        this.details.initials !== initials ||
        this.details.profilePicUrl !== data.payload.profilePicUrl
      ) {
        this.details = {
          id: data.payload.uid,
          userType: data.payload.ut,
          level: data.payload.ul,
          email: data.payload.email,
          name: data.payload.userName,
          forename: data.payload.userForename,
          surname: data.payload.userSurname,
          initials: initials,
          profilePicUrl: data.payload.profilePicUrl
        }
        updateSentryScope(data.payload)
      }
    },

    /**
     * Logs in a user with credentials
     * @param credentials - Login credentials object
     * @param redirectTo - Route to redirect to after successful login
     * @returns Promise with login result
     */
    async login(credentials: LoginCredentials, redirectTo: string = '/'): Promise<LoginResponse> {
      this.isAuthenticating = true
      const params = new URLSearchParams({
        email: credentials.e,
        password: credentials.p
      })

      const loginApiRequest = useApiRequest()

      try {
        const response = await loginApiRequest.send({
          endpoint: '/v1/auth?' + params,
          method: 'GET'
        })
        this.isAuthenticating = false

        if (!response.success) {
          this.isLoggedIn = false
          return {
            success: false,
            message: 'Credentials Incorrect'
          }
        } else {
          await this.setUserDetails(response.data as AuthData)
          await updateAbility(this.router)
          this.isLoggedIn = true

          // Initialize other stores after successful login
          await this.initializeAfterLogin()

          this.router?.push(redirectTo)

          return { success: true }
        }
      } catch (err: unknown) {
        console.error(err)
        this.isAuthenticating = false
        this.isLoggedIn = false

        const errorMessage = err instanceof Error ? err.message : 'Login failed'
        alertToast('Login failed', errorMessage, 'error')

        throw err
      }
    },

    /**
     * Verifies user authentication
     * @returns Promise with verification result
     */
    async verify(): Promise<VerifyResponse> {
      this.isVerifying = true

      try {
        const verifyApiRequest = useApiRequest()
        const response = await verifyApiRequest.send({
          endpoint: '/v1/verify',
          method: 'GET',
          routeToLoginOnUnauthorized: false
        })

        if (response.status === 503) {
          console.warn('503 Under Maintenance')

          this.router?.push('/maintenance')
          throw Error('503 Under Maintenance')
        }

        if (!response.success) {
          if (response.status === 401) {
            this.router?.push('/login')
            throw Error('401 Unauthorized')
          } else {
            throw new Error('Error verifying account')
          }
        }

        await this.setUserDetails(response.data as AuthData)

        // Set logged in state before initializing stores
        this.isLoggedIn = true

        // Update user's abilities
        await updateAbility(this.router)

        // Initialize other stores after successful verification
        await this.initializeAfterLogin()

        this.isVerifying = false

        return {
          success: true,
          message: null
        }
      } catch (err: unknown) {
        this.isLoggedIn = false
        this.isVerifying = false

        // Check if this is a 401 session expiration error
        // useApiRequest already handles 401 and shows a toast, so skip duplicate notification
        const errorObj = err && typeof err === 'object' ? (err as Record<string, unknown>) : null
        const httpResponse = errorObj && 'http' in errorObj ? (errorObj.http as { status?: number } | null) : null
        const isSessionExpired =
          (errorObj && 'code' in errorObj && errorObj.code === 401) ||
          (httpResponse && httpResponse.status === 401) ||
          (errorObj &&
            'message' in errorObj &&
            typeof errorObj.message === 'string' &&
            errorObj.message.includes('Session expired')) ||
          (err instanceof Error && err.message.includes('Session expired'))

        const errorMessage =
          err instanceof Error
            ? err.message
            : errorObj && 'message' in errorObj && typeof errorObj.message === 'string'
              ? errorObj.message
              : 'Verification failed'

        // Only show toast for non-session-expired errors (session expired toast is already shown by useApiRequest)
        if (!isSessionExpired) {
          alertToast("Couldn't verify your session", errorMessage, 'warning')
        }

        return {
          success: false,
          message: errorMessage
        }
      }
    },

    /**
     * Signs out the user
     * @param routeToLogin - Whether to route to login page after signout
     */
    async signOut(routeToLogin: boolean = true): Promise<void> {
      try {
        useApiRequest()
          .send({
            endpoint: '/v1/logout',
            method: 'POST'
          })
          .then(response => {
            if (!response.success) {
              console.error('Logout failed', response?.errors)
              alertToast('Failed server logout', response?.message || 'Unknown error', 'warning')
            }
          })
      } catch (err: unknown) {
        console.error(err)
      } finally {
        // Cleanup activities regardless of server response.
        // Clear access selected via AccessStore
        const { useAccessStore } = await import('./AccessStore')
        const accessStore = useAccessStore()
        accessStore.clearAccessSelected()

        // remove ls items
        localStorage.removeItem('callCenterSelected')
        localStorage.removeItem('report-filter-call-center-selected')

        // Clear all stores
        this.clearAllStores()

        this.isLoggedIn = false
        if (routeToLogin) {
          this.router?.push('/login')
        }
      }
    },

    /**
     * Initializes dependent stores after successful login/verify
     */
    async initializeAfterLogin(): Promise<void> {
      if (!this.isLoggedIn || !this.details.id) return

      const userSettingsStore = useUserSettingsStore()
      const taskStore = useTaskStore()
      const socketStore = useSocketStore()

      // Fetch user settings
      try {
        const settingsApiRequest = useApiRequest()
        const settingsResponse = await settingsApiRequest.send({
          endpoint: '/v1/me/' + this.details.id + '/settings'
        })

        if (settingsResponse.success) {
          userSettingsStore.setAllSettings(settingsResponse.data as UserSettings)
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get settings'
        console.error('ERROR: ' + errorMessage)
        alertToast('Failed to get settings', errorMessage, 'error')
      }

      // Fetch all tasks (outstanding count is computed locally)
      await taskStore.fetchTasks()

      // Initialize socket
      socketStore.initializeSocket()
    },

    /**
     * Updates user roles
     * @param roles - Array of role strings
     */
    updateRoles(roles: string[]): void {
      this.roles = roles
    },

    /**
     * Updates access allowed for user
     * @param access - Access object with regions, manufacturers, groups, and dealerships
     */
    updateAccessAllowed(access: AccessAllowed): void {
      this.accessAllowed = access
    },

    /**
     * Clears all dependent stores on logout
     */
    clearAllStores(): void {
      const userSettingsStore = useUserSettingsStore()
      userSettingsStore.$reset()

      // Reset authorization data
      this.roles = []
      this.accessAllowed = {
        regions: [],
        manufacturers: [],
        groups: [],
        dealerships: []
      }

      const callCenterStore = useCallCenterStore()
      callCenterStore.$reset()

      const socketStore = useSocketStore()
      socketStore.disconnectSocket()
      socketStore.$reset()

      const taskStore = useTaskStore()
      taskStore.$reset()
    }
  }
})
